/**
 * ConversationInbox Component
 * 
 * Displays all active conversations (WhatsApp + Instagram DM) in a unified inbox.
 * Users can filter by platform, see last message preview, sentiment, and click
 * to open the LiveChat for any conversation.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import './ConversationInbox.css';

const PLATFORM_CONFIG = {
  whatsapp_message: { icon: '💬', label: 'WhatsApp', cssClass: 'whatsapp' },
  instagram_dm: { icon: '📸', label: 'Instagram DM', cssClass: 'instagram-dm' },
  facebook_message: { icon: '📘', label: 'Facebook', cssClass: 'facebook' },
  instagram_comment: { icon: '💬', label: 'IG Comment', cssClass: 'instagram-comment' },
};

function ConversationInbox() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [conversations, setConversations] = useState([]);
  const [total, setTotal] = useState(0);
  const [pausedCount, setPausedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filterPlatform, setFilterPlatform] = useState('all');
  const [filterPaused, setFilterPaused] = useState(false);

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: '100' });
      if (filterPaused) params.append('paused_only', 'true');

      const response = await apiClient.get(`/api/v1/manual-chat/conversations/active?${params}`);
      const data = response.data;

      let items = data.conversations || [];

      // Client-side platform filtering
      if (filterPlatform !== 'all') {
        items = items.filter(c => c.platform === filterPlatform);
      }

      setConversations(items);
      setTotal(data.total || 0);
      setPausedCount(data.paused_count || 0);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
      setError(err.response?.data?.detail || t('inbox.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [filterPlatform, filterPaused, t]);

  useEffect(() => {
    fetchConversations();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const getSentimentBadge = (sentiment) => {
    const colors = {
      positive: '#4CAF50',
      negative: '#F44336',
      neutral: '#FFC107',
      mixed: '#9C27B0',
    };
    if (!sentiment) return null;
    return (
      <span className="sentiment-chip" style={{ backgroundColor: colors[sentiment] || '#9E9E9E' }}>
        {t(`analytics.sentiment.${sentiment}`, sentiment)}
      </span>
    );
  };

  const getPlatformBadge = (platform) => {
    const cfg = PLATFORM_CONFIG[platform] || { icon: '❓', label: platform, cssClass: '' };
    return (
      <span className={`platform-chip ${cfg.cssClass}`}>
        {cfg.icon} {cfg.label}
      </span>
    );
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { day: 'numeric', month: 'short' }) +
      ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Unique platforms for the filter
  const availablePlatforms = [...new Set(conversations.map(c => c.platform))];

  return (
    <div className="conversation-inbox">
      <div className="inbox-header">
        <div className="inbox-title-row">
          <button className="btn-back" onClick={() => navigate('/')}>
            ← {t('common.back_to_dashboard')}
          </button>
          <h2>{t('inbox.title', 'Conversations')}</h2>
        </div>

        <div className="inbox-stats">
          <span className="stat-chip">
            {t('inbox.totalConversations', 'Total')}: <strong>{total}</strong>
          </span>
          <span className="stat-chip paused">
            ⏸ {t('inbox.pausedConversations', 'AI Paused')}: <strong>{pausedCount}</strong>
          </span>
        </div>

        {/* Filters */}
        <div className="inbox-filters">
          <div className="filter-group">
            <label>{t('inbox.filterPlatform', 'Platform')}:</label>
            <select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value)}
            >
              <option value="all">{t('inbox.allPlatforms', 'All')}</option>
              <option value="whatsapp_message">💬 WhatsApp</option>
              <option value="instagram_dm">📸 Instagram DM</option>
              {availablePlatforms.includes('facebook_message') && (
                <option value="facebook_message">📘 Facebook</option>
              )}
            </select>
          </div>

          <label className="filter-toggle">
            <input
              type="checkbox"
              checked={filterPaused}
              onChange={(e) => setFilterPaused(e.target.checked)}
            />
            {t('inbox.showPausedOnly', 'Show paused only')}
          </label>

          <button className="btn-refresh" onClick={fetchConversations} title={t('inbox.refresh', 'Refresh')}>
            🔄
          </button>
        </div>
      </div>

      {/* Content */}
      {loading && conversations.length === 0 ? (
        <div className="inbox-loading">
          <div className="loading-spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      ) : error ? (
        <div className="inbox-error">
          <p>⚠️ {error}</p>
          <button onClick={fetchConversations}>{t('inbox.retry', 'Retry')}</button>
        </div>
      ) : conversations.length === 0 ? (
        <div className="inbox-empty">
          <i className="bi bi-inbox"></i>
          <p>{t('inbox.noConversations', 'No conversations yet. Once contacts message you on WhatsApp or Instagram, they will appear here.')}</p>
        </div>
      ) : (
        <div className="inbox-list">
          {conversations.map((conv) => (
            <div
              key={conv.id}
              className={`inbox-item ${conv.ai_paused ? 'paused' : ''}`}
              onClick={() => navigate(`/live-chat/${conv.id}`)}
            >
              <div className="inbox-item-left">
                <div className="contact-avatar">
                  {PLATFORM_CONFIG[conv.platform]?.icon || '💬'}
                </div>
                <div className="contact-details">
                  <div className="contact-name-row">
                    <span className="contact-name">{conv.contact_name || conv.contact_id}</span>
                    {conv.ai_paused && <span className="paused-badge">⏸ {t('inbox.paused', 'Paused')}</span>}
                  </div>
                  <div className="contact-preview">
                    {conv.last_message_preview || t('inbox.noPreview', 'No messages yet')}
                  </div>
                  <div className="contact-meta-row">
                    {getPlatformBadge(conv.platform)}
                    {conv.agent_type && <span className="agent-chip">{conv.agent_type}</span>}
                    {getSentimentBadge(conv.overall_sentiment)}
                  </div>
                </div>
              </div>

              <div className="inbox-item-right">
                <span className="message-time">{formatTime(conv.last_message_at)}</span>
                <span className="message-count">{conv.message_count} {t('inbox.msgs', 'msgs')}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ConversationInbox;
